import { useEffect, useRef } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useDeleteTabMutation,
  useUpdateTabNameMutation,
  useGetTabLayoutQuery,
} from "@/store/api/dashboard-api";
import { TabsState } from "./reducer";
import { useTabsState } from "./use-tabs-state";
import { UseTabManagementProps } from "./types";
import { isTemp } from "./utils";
import store from "@/store";
import { syncRemovedWidgetsBaseUsage } from "@/store/api/dataset-base-usage-sync";
import { syncRemovedWidgetsMetricUsage } from "@/store/api/dataset-metric-usage-sync";
import { dissolveSingleMemberGroups } from "./group-utils";

export const useTabApiSync = (
  state: TabsState,
  actions: ReturnType<typeof useTabsState>["actions"],
  props: UseTabManagementProps,
  saveStructure: (nextTabs: TabData[], previousTabs: TabData[]) => Promise<void>,
) => {
  const { localTabs, deleteConfirmation } = state;
  const { setDeleteHasContent, setLocalTabs } = actions;

  const debouncedTabs = useDebounce(localTabs, 1000);

  const [deleteTab] = useDeleteTabMutation();
  const [updateName] = useUpdateTabNameMutation();

  // Track which tabs have been updated to prevent duplicate API calls
  const updatedTabsRef = useRef<Set<string>>(new Set());
  const previousDebouncedTabsRef = useRef<TabData[]>([]);

  // save tabs to parent on debounced change + propagate name updates
  useEffect(() => {
    // Create a map of previous tab names for efficient lookup
    const previousTabMap = new Map(
      previousDebouncedTabsRef.current.map((t) => [t.id, t.label]),
    );

    // Track tabs that actually changed in this debounce cycle
    const changedTabs: TabData[] = [];

    debouncedTabs.forEach((t) => {
      const previousLabel = previousTabMap.get(t.id);
      const original = props.tabs.find((o) => o.id === t.id);

      // Only process if the label actually changed from the previous debounced state
      // For existing tabs (in props.tabs), also check if different from server state
      // For new tabs (not in props.tabs yet), allow the update if label changed
      const shouldUpdate =
        previousLabel !== t.label &&
        (!original || // New tab not yet in server state
          original.label !== t.label); // Existing tab with different label

      if (shouldUpdate) {
        changedTabs.push(t);
      }
    });

    // Update the ref to current debounced state
    previousDebouncedTabsRef.current = debouncedTabs;

    // Only make API calls for tabs that actually changed
    if (changedTabs.length > 0) {
      changedTabs.forEach((t) => {
        // Skip temporary tabs - they don't exist on the backend yet
        // They'll be updated once the backend creates them with real IDs
        if (isTemp(t.id)) {
          return;
        }

        // Create a unique key for this specific update
        const updateKey = `${t.id}-${t.label}`;

        // Only make the API call if we haven't already made it for this exact change
        if (!updatedTabsRef.current.has(updateKey)) {
          updatedTabsRef.current.add(updateKey);

          updateName({ tabId: t.id, name: t.label })
            .unwrap()
            .then(() => {
              // Only call onSave after API succeeds
              props.onSave(debouncedTabs);

              // Clean up old update keys for this tab to prevent memory leak
              Array.from(updatedTabsRef.current).forEach((key) => {
                if (key.startsWith(`${t.id}-`) && key !== updateKey) {
                  updatedTabsRef.current.delete(key);
                }
              });
            })
            .catch((e) => {
              console.error("Failed to update tab name:", e);
              // Remove the failed update key so it can be retried
              updatedTabsRef.current.delete(updateKey);
            });
        }
      });
    }
  }, [debouncedTabs, props, updateName]);

  // when delete modal is open, fetch layout to know if it has content
  const tabIdForQuery = deleteConfirmation?.tabId ?? "";
  const shouldSkip = !tabIdForQuery || !props.dashboardId;
  const { data: tabLayoutData } = useGetTabLayoutQuery(
    { dashboardId: props.dashboardId || "", tabId: tabIdForQuery },
    {
      skip: shouldSkip,
    },
  );

  useEffect(() => {
    if (!shouldSkip && tabLayoutData) {
      const hasContent =
        Array.isArray((tabLayoutData as any).items) &&
        (tabLayoutData as any).items.length > 0;
      setDeleteHasContent(hasContent);
    }
  }, [shouldSkip, tabLayoutData, setDeleteHasContent]);

  // delete helper
  const confirmDelete = async (forceDelete: boolean) => {
    const dc = state.deleteConfirmation;
    if (!dc) return;

    // Capture the tab's widgets before deletion so their base usage can be
    // reconciled — one PATCH per affected dataset (see syncRemovedWidgetsBaseUsage).
    const removedWidgets = tabLayoutData?.widgets ?? [];

    try {
      await deleteTab({
        tabId: dc.tabId,
        forceDelete,
        dashboardId: props.dashboardId,
      }).unwrap();

      if (props.dashboardId && removedWidgets.length > 0) {
        void syncRemovedWidgetsBaseUsage(
          store.dispatch,
          store.getState,
          props.dashboardId,
          removedWidgets,
        );
        void syncRemovedWidgetsMetricUsage(
          store.dispatch,
          store.getState,
          props.dashboardId,
          removedWidgets,
        );
      }

      // update local state immediately for UI feedback
      const updated = state.localTabs.filter((t) => t.id !== dc.tabId);
      const dissolved = dissolveSingleMemberGroups(updated);
      setLocalTabs(dissolved);

      // update parent state
      props.onSave(dissolved);
      props.onRemoveTab?.(dc.tabId);

      // if removing this tab left its group with one member, persist the
      // ungrouping — deleteTab only removes the tab, it doesn't know about groups
      if (dissolved !== updated) {
        void saveStructure(dissolved, updated);
      }
    } catch (e) {
      console.error("Failed to delete tab:", e);
      // let RTK Query rollback handle cache; keep modal open for user retry
      throw e;
    }
  };

  return { confirmDelete, tabLayoutData } as const;
};
